import type { NotificationReaderContext } from '@/modules/notifications/reads.api'
import { useMemo, useRef } from 'react'
import { useClientPortalLayout } from '@/contexts/ClientPortalLayoutContext'
import { useWorkspaceSession } from '@/hooks/useWorkspaceSession'
import { getCurrentClientPortalContext } from '@/lib/api/client-portal-context'

import { isValidUUID } from '@scrambl/shared'
import {
  linkAbortSignals,
  SessionChangedError,
} from '@scrambl/shared/session-scope'

/** Portal account access owns an entity route, even without a staff workspace. */
export function useNotificationPreferenceSession() {
  const workspace = useWorkspaceSession()
  const portalEntityId = getCurrentClientPortalContext()?.entityId ?? null
  const portal = useClientPortalLayout()
  const portalOrganizationId =
    portal.currentEntity?.id === portalEntityId
      ? portal.currentEntity.organization.id
      : null
  const organizationId = portalEntityId
    ? portalOrganizationId
    : workspace.organizationId
  const preview = portalEntityId !== null && portal.isInternalViewer
  const identity = JSON.stringify([portalEntityId, organizationId, preview])
  const currentIdentity = useRef(identity)
  currentIdentity.current = identity
  return useMemo(() => {
    const enabled =
      !!workspace.ownerUserId &&
      !!organizationId &&
      isValidUUID(organizationId) &&
      (portalEntityId === null
        ? workspace.enabled
        : isValidUUID(portalEntityId))
    const assertCurrent = () => {
      workspace.assertUnchanged()
      if (
        !enabled ||
        workspace.signal.aborted ||
        currentIdentity.current !== identity ||
        (getCurrentClientPortalContext()?.entityId ?? null) !== portalEntityId
      )
        throw new SessionChangedError()
    }
    return {
      enabled,
      ownerUserId: workspace.ownerUserId,
      organizationId,
      assertUnchanged: workspace.assertUnchanged,
      portalEntityId,
      preview,
      key:
        portalEntityId === null
          ? { workspaceSession: workspace.key, portalEntityId }
          : {
              workspaceSession: workspace.key,
              portalEntityId,
              preferenceOrganizationId: organizationId,
              preview,
            },
      assertCurrent,
      async run<T>(
        signal: AbortSignal,
        action: (context: NotificationReaderContext) => Promise<T>,
      ): Promise<T> {
        assertCurrent()
        // The portal guard owns access. A staff-workspace requirement cannot replace it.
        const linked = linkAbortSignals([signal, workspace.signal])
        try {
          linked.signal.throwIfAborted()
          const result = await action({
            organizationId: organizationId!,
            userId: workspace.ownerUserId!,
            signal: linked.signal,
          })
          assertCurrent()
          linked.signal.throwIfAborted()
          return result
        } finally {
          linked.dispose()
        }
      },
    }
  }, [workspace, portalEntityId, organizationId, preview, identity])
}
